import { SeededRNG } from './rng/SeededRNG';

/**
 * Generic fixed-step deterministic game simulation.
 *
 * Goals:
 * - Deterministic client/server runs: same `nonce` + same inputs => same score.
 * - Minimal, agent-friendly API surface.
 * - Records all inputs in a packed interval format for submission + replay.
 *
 * How Inputs Work:
 * - Each tick you call `update(inputs)` with the set of active inputs for that tick.
 * - Inputs are keyed by `id` and optionally carry a numeric payload in `data`.
 * - If an input is unchanged across ticks, it is packed as a single interval segment:
 *   `[...data, startTick, endTick]` (endTick is exclusive).
 * - "Id-only" inputs (e.g. a key held down) use empty payload `data: []` and pack as `[startTick, endTick]`.
 *
 * Example
 * 
 * client:
 * ```ts
 * // same `createGameSimulation` as in the server
 * const sim = createGameSimulation({
 *   fixedTimestep: 1,
 *   resetFn: ({ nonce, simRng }) => {
 *     // init world
 *   },
 *   updateFn: ({ inputs, simRng }) => {
 *     // step world using inputs
 *   },
 *   endFn: ({ simRng }) => 123, // score
 * });
 *
 * sim.reset(['player', 'seed']);
 * sim.update([{ id: 'key:Space', data: [] }]); // held key
 * sim.update([{ id: 'thrust', data: [0, 1] }]); // vector input
 * const sessionData = sim.end(); // send to server for validation
 * ```
 * 
 * server:
 * ```ts
 * // same `createGameSimulation` as in the client
 * const sim = createGameSimulation({
 *   fixedTimestep: 1,
 *   resetFn: ({ nonce, simRng }) => {
 *     // init world
 *   },
 *   updateFn: ({ inputs, simRng }) => {
 *     // step world using inputs
 *   },
 *   endFn: ({ simRng }) => 123, // score
 * });
 *
 * // `run` replays deterministically and checks score equality.
 * const scoreMatches = sim.run(sessionData);
 *
 * // If you need the computed score and packed output:
 * const replayed = sim.replay(sessionData);
 * // replayed.score
 * ```
 */


export type GameSimulationNonce = string[];

export type GameSimulationState = {
  tick: number;
  simTime: number;
  rngState: number;
};

export type GameSimulationInput = {
  /** Input stream identifier (packed per-id). Example: "thrust". */
  id: string;
  /**
   * Fixed-length numeric payload for this input stream.
   * Use `[]` for id-only inputs (e.g. keystrokes).
   */
  data?: number[];
};

export type PackedInputsById = Record<
  string,
  {
    /** How many numbers per segment: dataLength + 2 for [startTick, endTick]. */
    stride: number;
    /**
     * Packed segments: `[...data, startTick, endTick]` repeated.
     * Semantics: active on ticks `t` where `startTick <= t < endTick`.
     */
    packed: number[];
  }
>;

export type PackedInputsCursor = {
  reset: () => void;
  /**
   * Returns the active inputs for the given tick.
   * Must be called with strictly increasing ticks (t, t+1, t+2, ...).
   */
  getTickInputs: (tick: number) => GameSimulationInput[];
};

export type GameSimulationSessionData = {
  fixedTimestep: number;
  nonce: GameSimulationNonce;
  endTick: number;
  score: number;
  packedInputsById: PackedInputsById;
};

export type GameSimulationResetFn = (args: { nonce: GameSimulationNonce; simRng: SeededRNG }) => void;

export type GameSimulationUpdateFn = (args: {
  /** Active inputs for this tick (0..N). */
  inputs: GameSimulationInput[];
  simRng: SeededRNG;
}) => void;

export type GameSimulationEndFn = (args: {
  simRng: SeededRNG;
}) => number;

export type GameSimulationOptions = {
  fixedTimestep: number;
  resetFn: GameSimulationResetFn;
  updateFn: GameSimulationUpdateFn;
  endFn: GameSimulationEndFn;
};

const copyNonce = (nonce: GameSimulationNonce): GameSimulationNonce => {
  return nonce.slice();
};

const cloneNumberArray = (arr: number[]): number[] => {
  const sc: any = (globalThis as any).structuredClone;
  if (typeof sc === 'function') return sc(arr) as number[];
  return arr.slice();
};

const shallowEqNumberArray = (a: number[], b: number[]) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const copyPackedInputsById = (packedById: PackedInputsById): PackedInputsById => {
  const out: PackedInputsById = Object.create(null);
  for (const id of Object.keys(packedById)) {
    const entry = packedById[id];
    out[id] = { stride: entry.stride, packed: entry.packed.slice() };
  }
  return out;
};

type OpenSegment = { startTick: number; data: number[]; dataLen: number };

export class GameSimulation {
  readonly fixedTimestep: number;

  private readonly resetFn: GameSimulationResetFn;
  private readonly updateFn: GameSimulationUpdateFn;
  private readonly endFn: GameSimulationEndFn;

  private nonce: GameSimulationNonce | null = null;
  private simRng: SeededRNG | null = null;

  private tick: number = 0;
  private simTime: number = 0;
  private didEnd: boolean = false;

  // Packed interval recording (Rocket-Sheep style, generalized by input id).
  private readonly packedById: PackedInputsById = Object.create(null);
  private readonly openById: Map<string, OpenSegment> = new Map();
  private readonly dataLenById: Map<string, number> = new Map();

  constructor(opts: GameSimulationOptions) {
    if (!Number.isInteger(opts.fixedTimestep) || opts.fixedTimestep <= 0) {
      throw new Error(`fixedTimestep must be a positive integer (got ${opts.fixedTimestep})`);
    }

    this.fixedTimestep = opts.fixedTimestep;
    this.resetFn = opts.resetFn;
    this.updateFn = opts.updateFn;
    this.endFn = opts.endFn;
  }

  reset(nonce: GameSimulationNonce): GameSimulationState {
    if (!Array.isArray(nonce) || nonce.length === 0) {
      throw new Error('nonce_required');
    }

    this.nonce = copyNonce(nonce);
    this.simRng = new SeededRNG(copyNonce(this.nonce));
    this.tick = 0;
    this.simTime = 0;
    this.didEnd = false;
    this.openById.clear();
    this.dataLenById.clear();
    for (const k of Object.keys(this.packedById)) {
      delete this.packedById[k];
    }

    this.resetFn({ nonce: copyNonce(this.nonce), simRng: this.simRng });
    return this.getState();
  }

  update(inputs: GameSimulationInput[] = []): GameSimulationState {
    if (!this.simRng || !this.nonce) {
      throw new Error('must_reset_before_update');
    }
    if (this.didEnd) {
      throw new Error('cannot_update_after_end');
    }
    if (!Array.isArray(inputs)) {
      throw new Error('inputs_must_be_array');
    }

    // Canonicalize + validate (deterministic packing independent of array order).
    const activeInputs: GameSimulationInput[] = [];
    const seenIds: Set<string> = new Set();
    for (let i = 0; i < inputs.length; i++) {
      const inp = inputs[i] as any;
      const id = inp && typeof inp.id === 'string' ? inp.id : '';
      const data = inp && Array.isArray(inp.data) ? (inp.data as number[]) : [];
      if (!id) {
        throw new Error('invalid_input_id');
      }
      if (seenIds.has(id)) {
        throw new Error(`duplicate_input_id:${id}`);
      }
      seenIds.add(id);
      for (let j = 0; j < data.length; j++) {
        if (!Number.isFinite(data[j])) {
          throw new Error(`non_finite_input_data:${id}`);
        }
      }

      const expectedLen = this.dataLenById.get(id);
      if (expectedLen === undefined) {
        this.dataLenById.set(id, data.length);
      } else if (expectedLen !== data.length) {
        throw new Error(`input_data_len_changed:${id}`);
      }

      activeInputs.push({ id, data: cloneNumberArray(data) });
    }
    activeInputs.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

    // Update packed intervals per id.
    const activeById: Map<string, number[]> = new Map();
    for (let i = 0; i < activeInputs.length; i++) {
      activeById.set(activeInputs[i].id, activeInputs[i].data ?? []);
    }

    // Close segments that became inactive.
    for (const [id, open] of this.openById.entries()) {
      if (activeById.has(id)) continue;
      this._closeOpenSegment(id, open, this.tick);
      this.openById.delete(id);
    }

    // Open/extend/replace segments that are active.
    for (const [id, data] of activeById.entries()) {
      const open = this.openById.get(id);
      if (!open) {
        this.openById.set(id, { startTick: this.tick, data, dataLen: data.length });
        continue;
      }
      if (open.dataLen !== data.length) {
        throw new Error(`input_data_len_changed:${id}`);
      }
      if (shallowEqNumberArray(open.data, data)) {
        continue;
      }
      this._closeOpenSegment(id, open, this.tick);
      this.openById.set(id, { startTick: this.tick, data, dataLen: data.length });
    }

    this.updateFn({
      inputs: activeInputs,
      simRng: this.simRng,
    });
    this.tick += 1;
    this.simTime += this.fixedTimestep;
    return this.getState();
  }

  end(): GameSimulationSessionData {
    if (!this.simRng || !this.nonce) {
      throw new Error('must_reset_before_end');
    }
    if (this.didEnd) {
      throw new Error('end_called_twice');
    }
    this.didEnd = true;

    // Close any open input segments at endTick.
    const endTick = this.tick;
    for (const [id, open] of this.openById.entries()) {
      this._closeOpenSegment(id, open, endTick);
    }
    this.openById.clear();

    const score = this.endFn({ simRng: this.simRng });

    return {
      fixedTimestep: this.fixedTimestep,
      nonce: copyNonce(this.nonce),
      endTick,
      score,
      packedInputsById: copyPackedInputsById(this.packedById),
    };
  }

  getState(): GameSimulationState {
    if (!this.simRng) {
      return { tick: this.tick, simTime: this.simTime, rngState: 0 };
    }
    return {
      tick: this.tick,
      simTime: this.simTime,
      rngState: this.simRng.getState(),
    };
  }

  replay(session: Omit<GameSimulationSessionData, 'score'> & { score?: number }): GameSimulationSessionData {
    if (!session || typeof session !== 'object') {
      throw new Error('invalid_session');
    }
    if (session.fixedTimestep !== this.fixedTimestep) {
      throw new Error('fixed_timestep_mismatch');
    }
    if (!Number.isInteger(session.endTick) || session.endTick < 0) {
      throw new Error('invalid_endTick');
    }
    if (!Array.isArray(session.nonce) || session.nonce.length === 0) {
      throw new Error('invalid_nonce');
    }
    if (!session.packedInputsById || typeof session.packedInputsById !== 'object') {
      throw new Error('invalid_packedInputsById');
    }

    this.reset(session.nonce);

    const startsByTick: Map<number, GameSimulationInput[]> = new Map();
    const endsByTick: Map<number, string[]> = new Map();

    for (const id of Object.keys(session.packedInputsById)) {
      const entry = (session.packedInputsById as any)[id];
      const stride = entry && Number.isInteger(entry.stride) ? (entry.stride as number) : 0;
      const packed = entry && Array.isArray(entry.packed) ? (entry.packed as number[]) : null;
      if (!stride || stride < 2) {
        throw new Error(`invalid_stride:${id}`);
      }
      if (!packed) {
        throw new Error(`invalid_packed:${id}`);
      }
      if (packed.length % stride !== 0) {
        throw new Error(`packed_len_not_multiple_of_stride:${id}`);
      }

      let prevEnd = 0;
      for (let off = 0; off < packed.length; off += stride) {
        const data = packed.slice(off, off + (stride - 2));
        const startTick = packed[off + stride - 2];
        const endTick = packed[off + stride - 1];

        for (let i = 0; i < data.length; i++) {
          if (!Number.isFinite(data[i])) {
            throw new Error(`non_finite_input_data:${id}`);
          }
        }
        if (!Number.isInteger(startTick) || !Number.isInteger(endTick)) {
          throw new Error(`non_integer_ticks:${id}`);
        }
        if (startTick < 0 || endTick < 0 || endTick <= startTick) {
          throw new Error(`invalid_tick_range:${id}`);
        }
        if (startTick < prevEnd) {
          throw new Error(`overlapping_or_unsorted_segments:${id}`);
        }
        if (endTick > session.endTick) {
          throw new Error(`segment_past_endTick:${id}`);
        }
        prevEnd = endTick;

        const startList = startsByTick.get(startTick) || [];
        startList.push({ id, data });
        startsByTick.set(startTick, startList);

        const endList = endsByTick.get(endTick) || [];
        endList.push(id);
        endsByTick.set(endTick, endList);
      }
    }

    const activeById: Map<string, number[]> = new Map();
    for (let t = 0; t < session.endTick; t++) {
      const toEnd = endsByTick.get(t);
      if (toEnd) {
        for (let i = 0; i < toEnd.length; i++) {
          activeById.delete(toEnd[i]);
        }
      }
      const toStart = startsByTick.get(t);
      if (toStart) {
        for (let i = 0; i < toStart.length; i++) {
          activeById.set(toStart[i].id, toStart[i].data ?? []);
        }
      }

      const ids = Array.from(activeById.keys()).sort();
      const tickInputs: GameSimulationInput[] = [];
      for (let i = 0; i < ids.length; i++) {
        const data = activeById.get(ids[i]);
        if (!data) continue;
        tickInputs.push({ id: ids[i], data });
      }
      this.update(tickInputs);
    }

    return this.end();
  }

  run(session: GameSimulationSessionData): boolean {
    const computed = this.replay(session);
    return computed.score === session.score;
  }

  private _closeOpenSegment(id: string, open: OpenSegment, endTick: number) {
    if (endTick <= open.startTick) {
      return;
    }
    const stride = open.dataLen + 2;
    let entry = this.packedById[id];
    if (!entry) {
      entry = { stride, packed: [] };
      this.packedById[id] = entry;
    } else if (entry.stride !== stride) {
      throw new Error(`input_data_len_changed:${id}`);
    }
    entry.packed.push(...open.data, open.startTick, endTick);
  }
}

export const createGameSimulation = (opts: GameSimulationOptions) => {
  return new GameSimulation(opts);
};

/**
 * Helper for real-time client replays: expands packed inputs tick-by-tick without running the sim.
 *
 * The server should generally use `GameSimulation.replay(...)` to replay the whole run in one go.
 */
export const createPackedInputsCursor = (packedInputsById: PackedInputsById): PackedInputsCursor => {
  if (!packedInputsById || typeof packedInputsById !== 'object') {
    throw new Error('invalid_packedInputsById');
  }

  const startsByTick: Map<number, GameSimulationInput[]> = new Map();
  const endsByTick: Map<number, string[]> = new Map();

  for (const id of Object.keys(packedInputsById)) {
    const entry = (packedInputsById as any)[id];
    const stride = entry && Number.isInteger(entry.stride) ? (entry.stride as number) : 0;
    const packed = entry && Array.isArray(entry.packed) ? (entry.packed as number[]) : null;
    if (!id) {
      throw new Error('invalid_input_id');
    }
    if (!stride || stride < 2) {
      throw new Error(`invalid_stride:${id}`);
    }
    if (!packed) {
      throw new Error(`invalid_packed:${id}`);
    }
    if (packed.length % stride !== 0) {
      throw new Error(`packed_len_not_multiple_of_stride:${id}`);
    }

    let prevEnd = 0;
    for (let off = 0; off < packed.length; off += stride) {
      const data = packed.slice(off, off + (stride - 2));
      const startTick = packed[off + stride - 2];
      const endTick = packed[off + stride - 1];

      for (let i = 0; i < data.length; i++) {
        if (!Number.isFinite(data[i])) {
          throw new Error(`non_finite_input_data:${id}`);
        }
      }
      if (!Number.isInteger(startTick) || !Number.isInteger(endTick)) {
        throw new Error(`non_integer_ticks:${id}`);
      }
        if (startTick < 0 || endTick < 0 || endTick <= startTick) {
          throw new Error(`invalid_tick_range:${id}`);
        }
      if (startTick < prevEnd) {
        throw new Error(`overlapping_or_unsorted_segments:${id}`);
      }
      prevEnd = endTick;

      const startList = startsByTick.get(startTick) || [];
      startList.push({ id, data });
      startsByTick.set(startTick, startList);

      const endList = endsByTick.get(endTick) || [];
      endList.push(id);
      endsByTick.set(endTick, endList);
    }
  }

  const activeById: Map<string, number[]> = new Map();
  let lastTick = -1;

  const reset = () => {
    activeById.clear();
    lastTick = -1;
  };

  const getTickInputs = (tick: number): GameSimulationInput[] => {
    if (!Number.isInteger(tick) || tick < 0) {
      throw new Error('invalid_tick');
    }
    if (tick !== lastTick + 1) {
      throw new Error('replay_tick_out_of_order');
    }
    lastTick = tick;

    const toEnd = endsByTick.get(tick);
    if (toEnd) {
      for (let i = 0; i < toEnd.length; i++) {
        activeById.delete(toEnd[i]);
      }
    }
    const toStart = startsByTick.get(tick);
    if (toStart) {
      for (let i = 0; i < toStart.length; i++) {
        activeById.set(toStart[i].id, toStart[i].data ?? []);
      }
    }

    const ids = Array.from(activeById.keys()).sort();
    const out: GameSimulationInput[] = [];
    for (let i = 0; i < ids.length; i++) {
      const data = activeById.get(ids[i]);
      if (!data) continue;
      out.push({ id: ids[i], data });
    }
    return out;
  };

  return { reset, getTickInputs };
};
